import { ModuleLayout, Space, Text, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { DocumentStaticActionType } from '@views/documents/elements/DocumentStaticActions'
import { FileList, FileListItem, FileListItemType, FileListSkeleton } from '@views/documents/elements/FileList'

import { ModalId } from '../../../../enums/modalId'
import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { UNSORTED_FOLDER_ID } from '../../constants/unsortedFolderId'
import { useDocumentsDragAndDrop } from '../../contexts/DocumentsDragAndDropContext'
import { DocumentPreviewModal } from '../../elements/DocumentPreviewModal'
import { DocumentsActionsBar } from '../../elements/DocumentsActionsBar'
import { MoveItemModal } from '../../elements/MoveItemModal'
import { SharedDocumentsEmptyState } from '../../elements/SharedDocumentsEmptyState'
import { useCopyDocumentOrFolderLink } from '../../hooks/useCopyDocumentOrFolderLink'
import { useDmsUserRole } from '../../hooks/useDmsUserRole'
import { useDocumentDownload } from '../../hooks/useDocumentDownload'
import { useDocumentListState } from '../../hooks/useDocumentListState'
import { useDocumentPreviewActions } from '../../hooks/useDocumentPreviewActions'
import { useOpenDocumentIdFromStorage } from '../../hooks/useOpenDocumentIdFromStorage'
import { useRenameItem } from '../../hooks/useRenameItem'

export const UnsortedDocuments = (): ReactElement => {
  const [fileListKey, setFileListKey] = useState(0)
  const [moveItems, setMoveItems] = useState<{ id: string; type: FileListItemType }[]>([])
  const [closePreviewAfterMove, setClosePreviewAfterMove] = useState(false)
  const { t } = useTranslation()
  const { clearOpenDocumentId, openDocumentId } = useOpenDocumentIdFromStorage()
  const { open: openDeleteModal } = useModal(ModalId.DocumentsFileListDeleteModal)
  const { open: openMoveItemModal } = useModal(ModalId.DocumentsMoveItemModal)
  const { close: closePreviewModal, open: openPreviewModal } = useModal(ModalId.DocumentsPreviewModal)
  const { isLoading, isCpaExpertMode } = useDmsUserRole()
  const { organization } = useUserOrganization()
  const { copyLink } = useCopyDocumentOrFolderLink()
  const { renameItem } = useRenameItem()
  const { downloadDocument } = useDocumentDownload()
  const { startDraggingItem, stopDraggingItem } = useDocumentsDragAndDrop()

  const handleDeleteSuccess = useCallback(() => {
    setFileListKey((prev) => prev + 1)
  }, [])

  const {
    documents,
    isEmpty,
    isFolderContentsLoading,
    filteredItems,
    selectedIds,
    searchQuery,
    deleteItem,
    deleteBulkItems,
    handleCheckedChange,
    handleSearchChange,
  } = useDocumentListState({ onDeleteSuccess: handleDeleteSuccess })

  const { selectedDocument, handleRowClick, handlePreviewModalAction, selectAndOpenDocument } =
    useDocumentPreviewActions({
      documents,
      deleteItem,
      closePreviewModal,
      openPreviewModal,
      onCopyLink: (document) => copyLink(document.id, 'document'),
      onMove: (document) => {
        setMoveItems([{ id: document.id, type: 'document' }])
        setClosePreviewAfterMove(true)
        openMoveItemModal()
      },
    })

  const handleCopyLink = useCallback(
    (id: string) => {
      const item = filteredItems.find((listItem) => listItem.id === id)
      if (item) {
        copyLink(item.id, item.type)
      }
    },
    [copyLink, filteredItems],
  )

  const handleItemMoveSuccess = useCallback(() => {
    if (closePreviewAfterMove) {
      closePreviewModal()
      setClosePreviewAfterMove(false)
    }
  }, [closePreviewAfterMove, closePreviewModal])

  const handleBulkMoveToFolder = useCallback(() => {
    const itemsToMove = filteredItems
      .filter((item) => selectedIds.includes(item.id))
      .map(({ id, type }) => ({ id, type }))
    setMoveItems(itemsToMove)
    openMoveItemModal()
  }, [filteredItems, selectedIds, openMoveItemModal])

  const handleItemMove = useCallback(
    (id: string, type: FileListItemType) => {
      setMoveItems([{ id, type }])
      openMoveItemModal()
    },
    [openMoveItemModal],
  )

  const handleBulkDeleteConfirm = useCallback(() => {
    deleteBulkItems(selectedIds)
  }, [deleteBulkItems, selectedIds])

  const handleItemDragStart = useCallback(
    (item: FileListItem) => {
      startDraggingItem({ id: item.id, sourceFolderId: UNSORTED_FOLDER_ID, type: item.type })
    },
    [startDraggingItem],
  )

  const handleRename = useCallback(
    (id: string, newName: string) => {
      const item = filteredItems.find((i) => i.id === id)
      if (item) {
        renameItem({ id, type: item.type, name: newName })
      }
    },
    [filteredItems, renameItem],
  )

  const handleDownload = useCallback(
    (id: string) => {
      const targetDocument = documents.find((document) => document.id === id)
      if (targetDocument && organization?.id) {
        downloadDocument(organization.id, targetDocument.id, targetDocument.name)
      }
    },
    [documents, downloadDocument, organization?.id],
  )

  useEffect(() => {
    if (!openDocumentId || isFolderContentsLoading || !documents.some((document) => document.id === openDocumentId)) {
      return
    }

    selectAndOpenDocument(openDocumentId)
    clearOpenDocumentId()
  }, [clearOpenDocumentId, documents, isFolderContentsLoading, openDocumentId, selectAndOpenDocument])

  if (isLoading) {
    return (
      <ModuleLayout fullWidth title={t('documents.unsorted.title')} withMenuOffset={false}>
        <FileListSkeleton />
      </ModuleLayout>
    )
  }

  if (!isCpaExpertMode) {
    return (
      <Navigate
        to={getOrganizationViewUrl(organization?.url, reactRoute.organization.clientDocuments.getMainRoute())}
        replace
      />
    )
  }

  return (
    <>
      <ModuleLayout fullWidth title={t('documents.unsorted.title')} withMenuOffset={false}>
        <Text variant="bodyMD" colorVariant="secondary">
          {t('documents.unsorted.subtitle')}
        </Text>
        <Space size="s24" />
        <DocumentsActionsBar
          isEmpty={isEmpty}
          isLoading={isFolderContentsLoading}
          onDelete={openDeleteModal}
          onMoveToFolder={handleBulkMoveToFolder}
          onSearchChange={handleSearchChange}
          placeholder={t('documents.unsorted.search_placeholder')}
          searchQuery={searchQuery}
          selectedIds={selectedIds}
        />
        {filteredItems.length === 0 && !isFolderContentsLoading ? (
          <SharedDocumentsEmptyState />
        ) : (
          <FileList
            key={fileListKey}
            isLoading={isFolderContentsLoading}
            items={filteredItems}
            onBulkDelete={handleBulkDeleteConfirm}
            onCheckedChange={handleCheckedChange}
            onCopyLink={handleCopyLink}
            onDelete={deleteItem}
            onDownload={handleDownload}
            onItemDragEnd={stopDraggingItem}
            onItemDragStart={handleItemDragStart}
            onMove={handleItemMove}
            onRename={handleRename}
            onRowClick={handleRowClick}
            selectedCount={selectedIds.length}
          />
        )}
      </ModuleLayout>
      {moveItems.length > 0 && (
        <MoveItemModal
          id={ModalId.DocumentsMoveItemModal}
          items={moveItems}
          onMoveSuccess={handleItemMoveSuccess}
          sourceFolderId={UNSORTED_FOLDER_ID}
        />
      )}
      {selectedDocument && (
        <DocumentPreviewModal
          document={selectedDocument}
          id={ModalId.DocumentsPreviewModal}
          onAction={handlePreviewModalAction}
          visibleActions={[
            DocumentStaticActionType.CopyLink,
            DocumentStaticActionType.Move,
            DocumentStaticActionType.Delete,
          ]}
        />
      )}
    </>
  )
}
